import React, { useState, useEffect, useCallback, useRef } from "react";
import { bindActionCreators } from "redux";
import actions from "../../actions";
import { connect } from "react-redux";
import { getAllSubscriptions } from "../../apis";
import { toast } from "react-toastify";
import Pagination from "../../components/Pagination/Pagination";
import { useNavigate, useLocation } from "react-router-dom";

const Subscriptions = ({
  logoutRequest,
  startLoadingRequest,
  stopLoadingRequest,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [total, setTotal] = useState(null);

  const fetch = useCallback(
    async (page, rowsPerPage, query) => {
      try {
        startLoadingRequest();
        const apiResponse = await getAllSubscriptions(page, rowsPerPage, query);
        if (apiResponse?.data?.data?.length > 0) {
          setData(apiResponse?.data?.data);
          setTotal(apiResponse?.data?.totalCount);
          setCurrentPage(apiResponse?.data?.currentPage);
        } else {
          setData([]);
          setTotal(0);
        }
        stopLoadingRequest();
      } catch (error) {
        stopLoadingRequest();
        if (error?.response?.status === 401) {
          toast.error(error.response.data.message);
          logoutRequest();
        } else if (error?.response?.status === 403) {
          toast.error(error.response.data.message);
        }
      }
    },
    [logoutRequest, startLoadingRequest, stopLoadingRequest]
  );

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  // Fetch data when debounced search changes
  useEffect(() => {
    if (debouncedSearch.trim() !== "") {
      // When searching, always start from page 1 and use higher rows per page
      setCurrentPage(1);
      fetch(1, 100, debouncedSearch.trim());
      // Update URL to reflect search state
      navigate(`/subscriptions?page=1&rowsPerPage=100&search=${encodeURIComponent(debouncedSearch.trim())}`, { replace: true });
    } else {
      // When no search, reset to normal pagination
      setCurrentPage(1);
      setRowsPerPage(10);
      fetch(1, 10, "");
      // Update URL to remove search parameter
      navigate(`/subscriptions?page=1&rowsPerPage=10`, { replace: true });
    }
  }, [debouncedSearch, fetch, navigate]);

  // Sync page and rowsPerPage with URL (only when not searching)
  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const page = parseInt(queryParams.get("page") || "1", 10);
    const rows = parseInt(queryParams.get("rowsPerPage") || "10", 10);
    const searchParam = queryParams.get("search") || "";
    
    // Only sync URL params if we're not in the middle of a search operation
    if (searchParam === "" && debouncedSearch === "") {
      setCurrentPage(page);
      setRowsPerPage(rows);
      fetch(page, rows, "");
    } else if (searchParam !== "" && searchParam === debouncedSearch) {
      // Sync search from URL if it matches our current debounced search
      setSearchValue(searchParam);
    }
  }, [search, fetch, debouncedSearch]);

  const searchChangeHandler = useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const resetSearchHandler = useCallback(() => {
    setSearchValue("");
  }, []);

  function formatDate(ms) {
    const date = new Date(ms);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handlePageChange = (newPage) => {
    if (debouncedSearch.trim() !== "") {
      // If searching, maintain search parameters
      navigate(`/subscriptions?page=${newPage}&rowsPerPage=100&search=${encodeURIComponent(debouncedSearch.trim())}`);
    } else {
      // Normal pagination
      navigate(`/subscriptions?page=${newPage}&rowsPerPage=${rowsPerPage}`);
    }
  };

  return (
    <div className="section">
      <h1>Subscriptions</h1>
      <div className="wrapper">
        <div className="table-wrapper">
          <div className="table-header">
            <div className="search">
              <div className="input-wrapper">
                <input
                  value={searchValue}
                  onChange={searchChangeHandler}
                  placeholder="Search..."
                />
                {searchValue?.trim() && (
                  <button onClick={resetSearchHandler} style={{ marginLeft: '8px' }}>
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="px-4 py-3">S.No</th>
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Price (₹)</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Start Date</th>
                  <th className="px-4 py-3">End Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Store</th>
                  <th className="px-4 py-3">Country</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((item, index) => (
                  <tr
                    key={item._id}
                    // style={{ cursor: "pointer" }}
                  >
                    <td>{(currentPage - 1) * (debouncedSearch.trim() ? 100 : rowsPerPage) + index + 1}</td>
                    <td>{item?.userId?.username}</td>
                    <td>{item?.product_id}</td>
                    <td>{(item?.price_in_purchased_currency / 100).toFixed(2)}</td>
                    <td>{item?.type}</td>
                    <td>{formatDate(item?.purchased_at_ms)}</td>
                    <td>{formatDate(item?.expiration_at_ms)}</td>
                    <td>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor: item.status === "active" ? "#d1fae5" : "#fee2e2",
                          color: item.status === "active" ? "#065f46" : "#991b1b",
                          textTransform: "capitalize",
                        }}
                      >
                        {item?.status}
                      </span>
                    </td>
                    <td>{item?.store}</td>
                    <td>{item?.country_code}</td>
                  </tr>
                ))}
                {data?.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-6 text-center text-gray-400">
                      No subscriptions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {data?.length > 0 && (
            <Pagination
              pageInfo={{
                page: currentPage,
                total: Math.ceil(total / (debouncedSearch.trim() ? 100 : rowsPerPage)),
                totaluser:total,
                rowsPerPage: debouncedSearch.trim() ? 100 : rowsPerPage,
              }}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logoutRequest: actions.auth.logout,
      stopLoadingRequest: actions.loader.stopLoading,
      startLoadingRequest: actions.loader.startLoading,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(Subscriptions);